<!--
  AccountDefaults -- /distributor/account/defaults (single page).

  Card: Distributor Onboarding & Account (https://trello.com/c/QV2U4KhF)
  Pebble: Defaults page IA -- collapse pricing/branding sub-pages into
  one page.

  IA (2026-06-24): the Defaults surface was a tile-chooser landing that
  navigated to two sub-routes (/defaults/pricing, /defaults/branding),
  each of which held a single setting plus explanatory text. Per Jake,
  the settings moved to stacked cards on this one page; the sub-routes
  are gone (removed from the router).

  2026-08-24 (Jake): the "Default brand color" card was REMOVED from
  this page, leaving Pricing as the only card. This is a UI-ONLY
  removal -- ``distributors.default_brand_color`` still exists, is
  still writable from the superadmin app
  (``api/src/endpoints/superadmin_distributors.py``), and is still read
  at store-creation time (``api/src/services/distributor_summary.py``).
  Distributors simply no longer self-serve it. Do not "restore" the
  card on the theory that the value is orphaned; it is not.

  Capability gating: the route itself stays gated on the read-tier
  ``distributor.account.read`` so Builders can VIEW the page. Per-setting
  WRITE authorization is enforced at the card level here, via
  usePermissions().can(...):
    * Pricing card edits require ``distributor.pricing.defaults``.
  ``distributor.branding.defaults`` is NOT consulted here any more --
  it still gates the superadmin write path, it just has no card on this
  page to gate. The backend endpoints enforce their caps server-side as
  defense in depth.
-->
<template>
    <section
        class="dist-account-defaults"
        data-testid="dist-account-defaults-landing"
    >
        <div class="d-flex align-center">
            <h1
                class="dist-account-defaults__title"
                data-testid="dist-account-defaults-title"
            >Store defaults</h1>
            <HelpIcon
                audience="owner"
                slug="set-up-your-business"
                heading="store-defaults"
            />
        </div>
        <p class="dist-account-defaults__lede">
            Settings every new store starts with. Each store can override
            them.
        </p>

        <div class="dist-account-defaults__cards">
            <div
                class="dist-account-defaults__card"
                data-testid="dist-account-defaults-card-pricing"
            >
                <DefaultsPricing :editable="canEditPricing" />
            </div>
        </div>
    </section>
</template>

<script>
import { computed } from "vue";
import DefaultsPricing from "./DefaultsPricing.vue";
import { usePermissions } from "@/composables/usePermissions";
import HelpIcon from "@/components/Help/HelpIcon.vue";

export default {
    name: "AccountDefaults",

    components: { HelpIcon, DefaultsPricing },

    setup() {
        const { can } = usePermissions();

        // Owner-only write cap for the Pricing card. Note this is a
        // PEER cap to ``distributor.branding.defaults``, not an alias
        // for it (bar-raiser P0 on PR-F) -- holding branding must not
        // unlock pricing.
        const canEditPricing = computed(() =>
            can("distributor.pricing.defaults")
        );

        return { canEditPricing };
    },
};
</script>

<style scoped>
.dist-account-defaults {
    max-width: 920px;
    margin: var(--tf-sp-6, 32px) auto;
    padding: 0 var(--tf-sp-4, 16px);
}
.dist-account-defaults__title {
    font-size: var(--tf-size-h1, 1.75rem);
    font-weight: 600;
    margin-bottom: var(--tf-sp-2, 8px);
    color: var(--tf-text, #1f2937);
}
.dist-account-defaults__lede {
    color: var(--tf-text-muted, #4b5563);
    line-height: 1.5;
    max-width: 640px;
    margin-bottom: var(--tf-sp-6, 32px);
}
.dist-account-defaults__cards {
    display: flex;
    flex-direction: column;
    gap: var(--tf-sp-5, 24px);
}
.dist-account-defaults__card {
    padding: var(--tf-sp-5, 20px);
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: var(--tf-radius-md, 10px);
    background: var(--tf-surface, var(--tf-bg));
}
</style>
